import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Badge from "../../../components/badges/Badges";
import TableList, { GridCol, GridRow } from "../../../components/table/TableList";
import { Messages, MessageSearchDefault, RdbMessageListTimestampToDate, SearchMessage } from "../../../model/Message";
import { MessageEditModal } from "./MessageEditModal";
import messagesQuery from '../../../firestore/Messages.query';
import { Spiner } from "../../../components/spinner/Spiner";
import Button from "../../../components/button/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { convertDateToString } from "../../../utility/ConvertUtily";
import { auth } from "../../../firebase";
import companiesQuery from "../../../firestore/Companies.query";
import { TopicsMap } from "../../../model/TopicsMap";
import { Company } from "../../../model/Companies";
import { OnlineUrls } from "../../../model/OnlineUrls";
import { ReplyButton } from "../../../model/ReplyButton";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../../features/alert.slice";
import { ClipboardDocumentListIcon } from "@heroicons/react/24/outline";
import { createEvenReplyButtons } from "../../../utility/CreateEventReplyButtonUtility";
import { MessagePreviewModal } from "./MessagePreviewModal";
import { MessagingCommonFunctions } from "../../../controller/MessagingCommonFunctionsController";
import { ConstMessage } from "../../../config/const";
import { requestRdbAdminMessages, requestRdbAdminMessagesCount } from "../../../api/Messages.api";

export const MessageList = () => {

  const dispatch = useDispatch();
  const navigation = useNavigate();
  const location = useLocation();

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  const messageCommonFunctions = new MessagingCommonFunctions();
  // URLパラメータからtenantId取得
  const { tenantId } = useParams<{ tenantId: string }>();

  const isDisabled = useRef(false);
  const selectNumber = useRef<number>(50);
  const deleteMessageId = useRef<string>(location.state || '');
  const upsertMessageId = useRef<string>('');

  const [messageList, setMessageList] = useState<Messages[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoad, setIsLoad] = useState(true);
  const [isOpenPreview, setIsOpenPreview] = useState<boolean>(false);
  const [topicsMap, setTopicsMap] = useState<TopicsMap[]>([]);
  const [company, setCompany] = useState<Company>();
  const [replyButtonSelected, setReplyButtonSelected] = useState<ReplyButton>();
  const [companyReplyButtonList, setCompanyReplyButtonList] = useState<ReplyButton[]>([]);
  const [defaultReplyButtonList, setDefaultReplyButtonList] = useState<ReplyButton[]>([]);
  const [eventReplyButtonList, setEventReplyButtonList] = useState<ReplyButton[]>([]);
  const [onlineUrlList, setOnlineUrlList] = useState<OnlineUrls[]>([]);
  const [onlineUrlSelected, setOnlineUrlSelected] = useState<string>('none');
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [base64Image, setBase64Image] = useState<string>('');
  const [previewData, setPreviewData] = useState<Messages>();
  const [isUpdate, setIsUpdate] = useState(false);
  const [gridRowLength, setGridRowLength] = useState<number>(0);
  const [selectPage, setSelectPage] = useState<number>(1);
  const [sortKey, setSortKey] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<string>('DESC');

  // 初期データ取得
  useEffect(() => {
    const f = async () => {
      // 団体情報を取得
      const company = await companiesQuery.getCompany(tenantId!);
      setCompany(company);
      // グループ一覧を作成
      const groupList = await companiesQuery.getTenantGroups(tenantId!);

      const topicsMap: TopicsMap[] = [{
        id: company.displayId,
        name: company.name,
        nameKana: company.nameKana,
        userCount: company.usersCount,
        deleteFlg: company.deleteFlg
      }];
      groupList.forEach((group) => {
        topicsMap.push({
          id: group.id,
          name: group.name,
          nameKana: group.nameKana,
          userCount: group.userCount,
          deleteFlg: group.deleteFlg
        });
      });
      setTopicsMap(topicsMap);

      // 団体のお知らせボタン取得
      const companyReplyButtonList = await companiesQuery.getReplyButtons(tenantId!);
      companyReplyButtonList.unshift({
        id: "undefined",
        name: "",
        buttonNumber: 0,
        buttonName: [],
        buttonColor: [],
        type: "",
        createUser: "",
        createAt: new Date(),
      });
      setCompanyReplyButtonList(companyReplyButtonList);
      setDefaultReplyButtonList(companyReplyButtonList);

      // イベント用返信ボタン作成
      const eventReplyButtons = createEvenReplyButtons();
      eventReplyButtons.forEach((button) => {
        button.tenantId = tenantId!;
      });
      setEventReplyButtonList(eventReplyButtons);

      // ZoomURL一覧取得
      const onlineUrls = await companiesQuery.getOnlineUrls(tenantId!);
      onlineUrls.unshift({
        id: "none",
        onlineType: "",
        name: "設定なし",
        url: "none",
        type: "",
        createUser: "",
        createAt: new Date()
      },
      {
        id: "input",
        onlineType: "",
        name: "直接URLを入力",
        url: "",
        type: "",
        createUser: "",
        createAt: new Date()
      });
      setOnlineUrlList(onlineUrls);
    };
    f();
  }, [currentUser?.uid, tenantId, isUpdate]);

  // お知らせ取得
  useEffect(() => {
    const f = async () => {
      if (deleteMessageId.current !== "" || upsertMessageId.current !== '') {
        // RDB反映待ちのため2秒後にお知らせ取得
        setTimeout(async () => {
          await getMessages();
          deleteMessageId.current = "";
          upsertMessageId.current = "";
          setIsLoad(false);
        }, 2000);
      } else {
        await getMessages();
        setIsLoad(false);
      }
    };
    f();
  }, [topicsMap, isUpdate, selectPage, sortKey, sortOrder]);

  // お知らせ一覧取得
  async function getMessages() {
    if (company && topicsMap.length > 0) {
      const searchMessageItem: SearchMessage = {
        ...MessageSearchDefault(),
        orderBy: sortKey,
        dir: sortOrder,
        select: 'topic',
        topic: [
          {id: 'all', title: 'all'},
          ...topicsMap.map(t => {return {id: t.id, title: t.name}})
        ]
      };
      const result = await requestRdbAdminMessages(company.id, currentUser?.uid ?? '', selectNumber.current.toString(), selectPage.toString(), searchMessageItem);
      if (result.success) {
        const messages = result.data.map((msg: any) => RdbMessageListTimestampToDate(msg));
        setMessageList(messages);
        const rdbMessagesCount = await requestRdbAdminMessagesCount(currentUser!.tenantId ?? "", currentUser?.uid, searchMessageItem);
        setGridRowLength(rdbMessagesCount);
      } else {
        console.log(result.data)
        dispatch(alertOpen(AlertValue("お知らせ一覧の取得に失敗しました。", "danger")));
      }
    }
  }

  // ページング処理
  const paging = useCallback((page: number) => {
    setSelectPage(page);
  }, []);

  // お知らせ一覧カラム
  const columns: GridCol[] = [
    {field: "publishState",headerName: "配信状態",style: "w-1/12"},
    {field: "topic",headerName: "配信先",style: "w-1/12"},
    {field: "replyButton",headerName: "返信ボタン",style: "w-1/12"},
    {field: "title",headerName: "タイトル",style: "w-2/12"},
    {field: "createAt",headerName: "作成日時",style: "w-1/12"},
    {field: "sendDate",headerName: "配信日時",style: "w-1/12"},
    {field: "menu",headerName: "",style: "w-1/12", sort:false}
  ];
  // グループ一覧をidごとにMap化する
  const topicsMapMap = new Map(topicsMap.map(group => [group.id, group]));
  
  // お知らせ一覧データ
  const rowList: GridRow[] = useMemo(() => {
    return messageList.map<GridRow>((item : Messages) => {
      const title = item.title === "" || item.title === null
        ? (<span className={"text-gray-300"}>タイトルなし</span>)
        : item.title;

      // 配信状態の切り替え
      let publishState = (<Badge color="green">配信済み</Badge>);
      let publishStateString = '配信済み';
      if(item.deliveredFlg) {
        publishState = (<Badge color="green">配信済み</Badge>);
        publishStateString = '配信済み';
      } else if(item.publishEndAt !== undefined && item.publishEndAt <= new Date()) {
        publishState = (<Badge color="gray">掲載終了</Badge>);
        publishStateString = '掲載終了';
      } else {
        publishState = (<Badge color="red">未配信</Badge>);
        publishStateString = '未配信';
      };
      if(item.draftFlg) {
        publishState = (<Badge color="blue">下書き</Badge>);
        publishStateString = '下書き';
      };
      // 返信ボタン
      const replyButton = item.replyButton && item.replyButton != "" ? (
        <Badge color="purple" className="ml-2 pr-1 pl-1">
          <ClipboardDocumentListIcon className="h-4 w-4" />
        </Badge>
      ) : '';
      // 配信先
      const topicElements = item?.topic?.map((groupId, index) => {
        const groupName =
          groupId === 'all'
            ? '全体'
            : topicsMapMap.get(groupId)?.name || groupId;

        return (
          <div key={`${groupId}-${index}`} className="mr-1 mb-1">
            <Badge color="yellow">{groupName}</Badge>
          </div>
        );
      });

      return {
        rowData: {
          publishState: publishState,
          title: title,
          replyButton: replyButton,
          createAt: convertDateToString(item.createAt),
          sendDate: convertDateToString(item.sendDate),
          topic: <div className="flex whitespace-normal">{topicElements}</div>,
          menu: (
            <Button
              size={"sm"}
              className={"px-3"}
              onClick={() => navigation(`${item.id}/info`)}
              children="詳細" />
          ),
        },
        sortableRowData: {
          publishState: publishStateString,
          topic: item?.topic?.at(0) ?? ''
        },
        style: "truncate max-w-0 whitespace-normal",
      };
    });
  }, [messageList])

  // お知らせプレビュー
  const onClickPreview = async (data: Messages) => {
    await messageDataCreate(data, 'preview');
  };

  // 新規お知らせ登録
  const onClickSubmit = async () => {
    await messageDataCreate(previewData!, 'send');
  };

  // お知らせ下書き
  const onDelivered = async (data: Messages) => {
    await messageDataCreate(data, 'draft');
  };

  // お知らせの下書き・登録時の共通処理
  const messageDataCreate = async (data: Messages, type: 'draft' | 'send' | 'preview')
    : Promise<void> => {
    if(!isDisabled.current){
      try{
        isDisabled.current = true;
        const {sendDateList, sendData} = await messageCommonFunctions.submit(
          data, type, onlineUrlSelected, currentUser,
          selectedEventType, base64Image, company, tenantId
        );

        // Firestore登録処理
        sendData.select = company?.displayId!;
        if(type === 'send' || type === 'draft') {
          // 配信または下書きの場合
          sendDateList.sort((a, b) => a > b ? 1 : -1);
          const result = await Promise.all(sendDateList.map( async (sendDateAndEventDateList) => {
            sendData.sendDate = sendDateAndEventDateList.sendDate;
            sendData.eventAt = sendDateAndEventDateList.eventAt ?? new Date();
            sendData.eventEndAt = sendDateAndEventDateList.eventEndAt ?? new Date();
            // 配信フラグ
            sendData.deliveredFlg = type === 'send' ? sendData.sendDate < new Date() : false;
            return await messagesQuery.addMessage(sendData, replyButtonSelected);
          }));
          if(result.length === 0){
            throw new Error();
          } else {
            upsertMessageId.current = result[0];
          }
          const alertMessage = type === 'draft' ? "下書き保存しました。" : "お知らせを作成しました";
          dispatch(alertOpen(AlertValue(alertMessage, "success")));
          if(type === 'send') {
            togglePreview();
          }
          toggle();
          setIsLoad(true);
          setIsUpdate(prev => !prev);
          isDisabled.current = false;
        } else {
          // プレビューの場合
          setPreviewData(data);
          togglePreview();
          isDisabled.current = false;
        }
      } catch (err: any) {
        if (err === "ERR_NETWORK") {
          dispatch(alertOpen(AlertValue("ネットワークに接続できません。", "danger")));
        } else {
          console.error(err)
          dispatch(alertOpen(AlertValue("保存に失敗しました", "danger")));
        };
        isDisabled.current = false;
      };
    }
  };

  const onReplyButtonSelect = (id: string) => {
    const replyButton: ReplyButton | undefined = companyReplyButtonList?.filter(
      (item) => item.id === id)[0];
    setReplyButtonSelected(replyButton);
  };

  const onOnlineUrlSelect = (url: string) => {
    setOnlineUrlSelected(url);
  };

  const onEventTypeSelect = (eventType: string) => {
    setSelectedEventType(eventType);
  };

  const onSendImage = (file: File) => {
    try{
      if(file.type !== 'image/jpeg' && file.type !== 'image/png'){
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_UNSUPPORT_IMG_FILE_TYPE, "danger")));
        return;
      };
      messageCommonFunctions.imageFileToBase64Image(file, setBase64Image);
    } catch (error: any){
      console.error(error);
      dispatch(alertOpen(AlertValue("アップロードに失敗しました", "danger")));
    }
  };

  const toggle = () => {
    setIsOpen(!isOpen);
    setOnlineUrlSelected('none');
    setReplyButtonSelected(undefined);
    setSelectedEventType('');
    setBase64Image('');
  };

  const togglePreview = () => {
    setIsOpenPreview(!isOpenPreview);
  }

  return (
    <div className="h-full min-w-[820px]">
      <MessageEditModal
        open={isOpen}
        toggle={toggle}
        closeOnClickOutside={true}
        children={undefined}
        message={undefined}
        company={company}
        onClickPreview={onClickPreview}
        onDelivered={onDelivered}
        onReplyButtonSelect={onReplyButtonSelect}
        replyButtonList={companyReplyButtonList}
        setReplyButtonList={setCompanyReplyButtonList}
        defaultReplyButtonList={defaultReplyButtonList}
        eventReplyButtonList={eventReplyButtonList}
        replyButtonSelected={replyButtonSelected}
        onOnlineUrlSelect={onOnlineUrlSelect}
        onEventTypeSelect={onEventTypeSelect}
        selectedEventType={selectedEventType}
        onSendImage={onSendImage}
        setBase64Image={setBase64Image}
        base64Image={base64Image}
        onlineUrlList={onlineUrlList}
        onlineUrlSelected={onlineUrlSelected}
      />
      <MessagePreviewModal
        children={undefined}
        open={isOpenPreview}
        toggle={togglePreview}
        closeOnClickOutside={false}
        data={previewData}
        company={company!}
        replyButtonSelected={replyButtonSelected}
        onClickSubmit={onClickSubmit}
      />
      <div className="flex items-center space-x-4 p-6 justify-between">
        <h2 className="flex text-xl font-semibold text-gray-900">
          お知らせ一覧
        </h2>
        <div>
          <Button
            size={"sm"}
            onClick={toggle}
            children={"お知らせ新規作成"}
          />
        </div>
      </div>
      <div className={"px-6"}>
        {isLoad === true
          ? <Spiner />
          : (
            <TableList
              header
              gridCol={columns}
              gridRow={rowList}
              defaultSortKey={columns[5].field}
              defaultSortOrder={"desc"}
              limit={selectNumber.current}
              gridRowLength={gridRowLength}
              pagingFunc={paging}
              parentSetSortKey={setSortKey}
              parentSetSortOrder={setSortOrder}
              externalCurrentPage={selectPage}
              setExternalCurrentPage={setSelectPage}
            />
          )
        }
      </div>
    </div>
  );
};
